import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  Search,
  UserPlus,
  Upload,
  ClipboardList,
  Stethoscope,
  Calendar,
  History,
  RefreshCw,
  ShieldCheck,
  Building2,
  Layers3,
  FileText,
  Image,
  DollarSign,
  Activity,
  AlertTriangle
} from "lucide-react";

export default function AlignersTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>

            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">
            Ajuda
          </Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">
            Guias
          </Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Alinhadores Invisíveis</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Alinhadores Invisíveis
        </h1>

        <p className="text-xl text-gray-600 font-semibold mb-6">
          Gestão completa de tratamentos ortodônticos com alinhadores
        </p>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-10">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>10 minutos de leitura</span>
          </div>

          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Atendimento Clínico
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            O módulo de <strong>Alinhadores Invisíveis</strong> do OdontoAssist AI
            foi desenvolvido para centralizar o acompanhamento clínico dos
            pacientes tratados com alinhadores ortodônticos.
          </p>

          <p>
            Com ele, a clínica pode organizar tratamentos, etapas, refinamentos,
            observações e histórico em um único ambiente, mantendo essas
            informações integradas ao prontuário, à agenda e aos demais recursos
            clínicos do paciente.
          </p>

          <p>
            O módulo também permite importar tratamentos de parceiros como a
            SouSmile, identificando pacientes já cadastrados e criando apenas os
            registros necessários, com mecanismos de prevenção de duplicidades.
          </p>

          <div className="bg-indigo-50 border border-indigo-200 rounded-3xl p-6 my-8">
            <h3 className="text-lg font-bold text-indigo-900 mt-0 mb-2">
              Um único fluxo para todo o tratamento
            </h3>
            <p className="text-indigo-800 mb-0">
              O profissional pode localizar o tratamento na Central de
              Alinhadores ou acessá-lo diretamente pela aba <strong>Alinhadores</strong> do prontuário do paciente.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
            Passo a Passo em Texto
          </h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                Central de Alinhadores
              </h3>
              <p>
                A página <strong>Alinhadores</strong> reúne os tratamentos da clínica em uma visão centralizada. Ela permite acompanhar rapidamente quais pacientes estão em tratamento e localizar os dados necessários para o atendimento.
              </p>
              <p>Na Central de Alinhadores você pode:</p>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold flex items-center gap-2 text-indigo-700 mb-2"><Search size={18} /> Localizar pacientes</h4>
                  <p className="text-sm text-gray-600">Utilize a pesquisa para encontrar rapidamente um tratamento pelo nome do paciente.</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold flex items-center gap-2 text-blue-700 mb-2"><ClipboardList size={18} /> Consultar tratamentos</h4>
                  <p className="text-sm text-gray-600">Visualize os principais dados de cada tratamento sem precisar abrir primeiro o prontuário.</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold flex items-center gap-2 text-purple-700 mb-2"><RefreshCw size={18} /> Acompanhar refinamentos</h4>
                  <p className="text-sm text-gray-600">Consulte a quantidade de refinamentos informada e acompanhe a continuidade do tratamento.</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold flex items-center gap-2 text-emerald-700 mb-2"><Activity size={18} /> Ver a situação atual</h4>
                  <p className="text-sm text-gray-600">Identifique rapidamente o status e a etapa registrada para o paciente.</p>
                </div>
              </div>

              <p className="mt-5">
                Ao selecionar um tratamento, o sistema abre os detalhes correspondentes e permite continuar o acompanhamento clínico.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                Cadastro de um Tratamento
              </h3>
              <p>
                Quando necessário, a clínica pode cadastrar manualmente um novo tratamento de alinhadores e vinculá-lo a um paciente já existente no OdontoAssist AI.
              </p>
              <p>Entre as informações que podem fazer parte do cadastro estão:</p>
              <ul>
                <li>paciente;</li>
                <li>profissional responsável;</li>
                <li>empresa ou fabricante dos alinhadores;</li>
                <li>status e etapa do tratamento;</li>
                <li>quantidade de alinhadores;</li>
                <li>refinamentos;</li>
                <li>datas importantes;</li>
                <li>observações clínicas e administrativas.</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mt-5">
                <p className="text-sm text-blue-800 mb-0">
                  <strong>Importante:</strong> confirme sempre se o paciente já possui cadastro antes de criar um novo. Isso evita prontuários duplicados e mantém todo o histórico clínico concentrado no mesmo registro.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                Importação de Pacientes e Tratamentos
              </h3>
              <p>
                Clínicas parceiras de fabricantes de alinhadores podem utilizar uma importação assistida para trazer os pacientes e tratamentos para o OdontoAssist AI.
              </p>
              <p>Durante a importação, o sistema pode:</p>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                  <h4 className="font-bold flex items-center gap-2 text-indigo-700 mb-2"><UserPlus size={18} /> Criar pacientes novos</h4>
                  <p className="text-sm text-gray-600">Quando não existe cadastro correspondente, o paciente pode ser criado com os dados disponíveis na planilha de origem.</p>
                </div>
                <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                  <h4 className="font-bold flex items-center gap-2 text-green-700 mb-2"><ShieldCheck size={18} /> Identificar pacientes existentes</h4>
                  <p className="text-sm text-gray-600">O sistema verifica os dados disponíveis para evitar a criação desnecessária de um segundo prontuário.</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                  <h4 className="font-bold flex items-center gap-2 text-purple-700 mb-2"><Upload size={18} /> Criar tratamentos</h4>
                  <p className="text-sm text-gray-600">Os dados do tratamento são vinculados ao paciente novo ou ao cadastro já existente.</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
                  <h4 className="font-bold flex items-center gap-2 text-amber-700 mb-2"><AlertTriangle size={18} /> Bloquear casos duvidosos</h4>
                  <p className="text-sm text-gray-600">Correspondências inseguras podem ser separadas para revisão manual antes da gravação definitiva.</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mt-5">
                <p className="text-sm text-amber-800 mb-0">
                  <strong>Atenção:</strong> a importação deve ser conferida antes da gravação definitiva. Dados divergentes, cadastros antigos ou nomes semelhantes precisam ser revisados para prevenir associações incorretas.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">4</span>
                Aba Alinhadores no Prontuário
              </h3>
              <p>
                O tratamento também pode ser acessado diretamente dentro do prontuário do paciente, pela aba <strong>Alinhadores</strong>.
              </p>
              <p>
                Essa aba reúne as informações relacionadas ao tratamento sem exigir que o profissional saia do prontuário durante o atendimento.
              </p>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mt-4">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3"><Stethoscope size={20} className="text-indigo-600" /> Informações disponíveis</h4>
                <ul className="mb-0">
                  <li>resumo do tratamento;</li>
                  <li>empresa ou fabricante;</li>
                  <li>profissional responsável;</li>
                  <li>status e etapa atual;</li>
                  <li>quantidade de alinhadores;</li>
                  <li>refinamentos;</li>
                  <li>datas e observações registradas.</li>
                </ul>
              </div>

              <p className="mt-5">
                Essa integração facilita a consulta simultânea da anamnese, odontograma, imagens, documentos, evolução clínica e demais dados do paciente.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">5</span>
                Acompanhamento e Evolução Clínica
              </h3>
              <p>
                As informações do módulo de alinhadores complementam o prontuário, mas não substituem o registro da evolução clínica realizada pelo profissional.
              </p>
              <p>Sempre que houver uma consulta ou ocorrência relevante, registre na evolução clínica informações como:</p>
              <ul>
                <li>entrega de novos alinhadores;</li>
                <li>adaptação e colaboração do paciente;</li>
                <li>tempo diário de uso relatado;</li>
                <li>troca de sequências ou etapas;</li>
                <li>perda ou quebra de alinhadores;</li>
                <li>necessidade de refinamento;</li>
                <li>intercorrências e orientações fornecidas;</li>
                <li>decisões clínicas tomadas durante o acompanhamento.</li>
              </ul>
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mt-5">
                <p className="text-sm text-green-800 mb-0"><strong>Boa prática:</strong> registre fatos clínicos relevantes logo após o atendimento. Isso melhora a continuidade do tratamento e preserva um histórico claro das decisões profissionais.</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">6</span>
                Integração com a Agenda
              </h3>
              <p>Os retornos do tratamento podem ser agendados normalmente pela Agenda Inteligente do OdontoAssist AI.</p>
              <p>Entre os atendimentos mais comuns estão:</p>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <h4 className="font-bold flex items-center gap-2 text-blue-700 mb-2"><Calendar size={18} /> Entrega de alinhadores</h4>
                  <p className="text-sm text-gray-600">Organize a entrega inicial ou de novas sequências ao paciente.</p>
                </div>
                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                  <h4 className="font-bold flex items-center gap-2 text-indigo-700 mb-2"><Activity size={18} /> Consultas de controle</h4>
                  <p className="text-sm text-gray-600">Programe avaliações periódicas para acompanhar adaptação, movimentação e colaboração.</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                  <h4 className="font-bold flex items-center gap-2 text-purple-700 mb-2"><RefreshCw size={18} /> Refinamentos</h4>
                  <p className="text-sm text-gray-600">Registre consultas relacionadas a escaneamentos, revisões e entregas de refinamentos.</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                  <h4 className="font-bold flex items-center gap-2 text-emerald-700 mb-2"><CheckCircle2 size={18} /> Revisão final</h4>
                  <p className="text-sm text-gray-600">Organize a avaliação de conclusão, documentação final e orientações de contenção.</p>
                </div>
              </div>

              <p className="mt-5">
                Ao abrir o agendamento e entrar no prontuário, o profissional pode consultar imediatamente a aba de alinhadores e os demais registros clínicos do paciente.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">7</span>
                Tratamento Integrado aos Demais Módulos
              </h3>
              <p>
                O módulo de alinhadores foi desenvolvido para funcionar como parte do prontuário completo do paciente, e não como um cadastro isolado.
              </p>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold flex items-center gap-2 text-indigo-700 mb-2"><Stethoscope size={18} /> Prontuário</h4>
                  <p className="text-sm text-gray-600">Consulte anamnese, odontograma, histórico e informações clínicas no mesmo paciente.</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold flex items-center gap-2 text-blue-700 mb-2"><Image size={18} /> Imagens e exames</h4>
                  <p className="text-sm text-gray-600">Centralize fotografias, radiografias, escaneamentos, PDFs e outros documentos visuais.</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold flex items-center gap-2 text-purple-700 mb-2"><FileText size={18} /> Documentos</h4>
                  <p className="text-sm text-gray-600">Mantenha contratos, termos, consentimentos e demais documentos relacionados ao atendimento.</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold flex items-center gap-2 text-emerald-700 mb-2"><DollarSign size={18} /> Financeiro</h4>
                  <p className="text-sm text-gray-600">Acompanhe cobranças, pagamentos e informações financeiras do paciente nos módulos correspondentes.</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">8</span>
                Histórico e Continuidade do Tratamento
              </h3>
              <p>
                Um acompanhamento bem documentado facilita a continuidade do tratamento, especialmente quando mais de um profissional ou membro da equipe participa do atendimento.
              </p>
              <p>Ao manter o cadastro de alinhadores e a evolução clínica atualizados, a clínica consegue identificar:</p>
              <ul>
                <li>quando o tratamento foi iniciado;</li>
                <li>qual profissional está responsável;</li>
                <li>qual é a situação atual;</li>
                <li>quantos alinhadores foram previstos;</li>
                <li>se houve refinamentos;</li>
                <li>quais intercorrências foram registradas;</li>
                <li>quais orientações foram fornecidas ao paciente;</li>
                <li>quando o tratamento foi concluído.</li>
              </ul>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mt-5">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2"><History size={20} className="text-indigo-600" /> Histórico clínico organizado</h4>
                <p className="text-sm text-gray-600 mb-0">
                  Utilize sempre a evolução clínica para documentar procedimentos, decisões e orientações. O cadastro de alinhadores apresenta o estado do tratamento, enquanto a evolução registra o que ocorreu em cada atendimento.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-3xl p-8 mt-12">
            <h3 className="text-2xl font-bold text-green-900 mt-0 mb-4 flex items-center gap-3"><ShieldCheck size={26} /> Boas práticas</h3>
            <ul className="space-y-3 mb-0">
              <li>confira o paciente antes de cadastrar um novo tratamento;</li>
              <li>mantenha o status e os refinamentos atualizados;</li>
              <li>registre intercorrências na evolução clínica;</li>
              <li>utilize a agenda para organizar entregas e retornos;</li>
              <li>mantenha imagens, exames e documentos no prontuário do paciente;</li>
              <li>revise correspondências antes de concluir importações de bases externas;</li>
              <li>não substitua a evolução clínica por observações administrativas do tratamento.</li>
            </ul>
          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
            <h3 className="text-2xl font-bold mb-4">Recursos do Módulo de Alinhadores</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Central única de pacientes em tratamento.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Acesso ao tratamento diretamente pelo prontuário do paciente.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Cadastro de status, etapas, alinhadores e refinamentos.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Integração com agenda e evolução clínica.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Integração com imagens, documentos e demais áreas do prontuário.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Importação assistida de pacientes e tratamentos.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Identificação de pacientes existentes para prevenção de duplicidades.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Suporte inicial a tratamentos importados da SouSmile.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} /><span>Estrutura preparada para diferentes fabricantes e parceiros.</span></li>
            </ul>
          </div>

          <div className="bg-white border border-gray-100 rounded-3xl p-8 mt-8 shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mt-0 mb-3 flex items-center gap-2"><Building2 size={22} className="text-indigo-600" /> Disponibilidade do módulo</h3>
            <p className="mb-0">
              A visualização do módulo depende da habilitação do recurso para a clínica. Quando ativo, a opção <strong>Alinhadores</strong> aparece no menu principal e na aba correspondente do prontuário.
            </p>
          </div>

          <div className="bg-white border border-gray-100 rounded-3xl p-8 mt-8 shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mt-0 mb-3 flex items-center gap-2"><Layers3 size={22} className="text-indigo-600" /> Organização recomendada</h3>
            <p className="mb-0">
              Use a Central de Alinhadores para a visão geral dos tratamentos e a aba do prontuário para o acompanhamento individual. Registre os acontecimentos clínicos na evolução e utilize os demais módulos para armazenar imagens, documentos, agendamentos e informações financeiras.
            </p>
          </div>
        </article>
      </main>
    </div>
  );
}
